import { get, find, isEmpty, last, isNil } from 'lodash';
import searchQuery from 'query-string';

// local dependency
import { getStore } from 'store';
import { REDUCER, CALL_TYPE } from 'constants/entity-names';
import { CALL_ACTIVITY_STATE, CALLER_TYPE_NAMES } from 'constants/common';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';

// get store
const store = getStore();

/**
 * @desc Check if ongoing call is inbound call.
 * @example
 * import { isInbound } from 'resolvers/callStateEntities';
 */
export const isInbound = () => {

    // get callType from store
    const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

    // get callTypeOptions from store
    const callTypeOptions = get( store.getState()[ REDUCER.APP_CONTEXT ], 'component.data.options.callType', [] );

    // get callTypeOption
    const callTypeOption = find( callTypeOptions, { value: callType } );

    return CALL_TYPE.INBOUND === get( callTypeOption, 'name', null );
};

/*************************/

/**
 * @desc Check if ongoing call is outbound call.
 * @example
 * import { isOutbound } from 'resolvers/callStateEntities';
 */
export const isOutbound = ( ) => {

    // get callType from store
    const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

    // if `callType` is empty
    if( isNil( callType ) ) {
        return false;
    }

    // get callTypeOptions from store
    const callTypeOptions = get( store.getState()[ REDUCER.APP_CONTEXT ], 'component.data.options.callType', [] );

    // get callTypeOption
    const callTypeOption = find( callTypeOptions, { value: callType } );

    return CALL_TYPE.OUTBOUND === get( callTypeOption, 'name', null );
};

/*************************/

/**
 * @desc Check if ongoing call is outbound call.
 * @example
 * import { getCallerTypeFromName } from 'resolvers/callStateEntities';
 */
export const getCallerTypeFromName = ( name ) => {

    // get callerTypeOptions from store
    const callerTypeOptions = get( store.getState()[ REDUCER.APP_CONTEXT ], 'component.data.options.callerType', [] );

    // get callerTypeOption
    const callerTypeOption = find( callerTypeOptions, { name: name } );

    // return the value of caller Type
    return get( callerTypeOption, 'value' );
};

/**
 * @desc Check if ongoing call is outbound call.
 * @example
 * import { getCallerTypeNameFromValue } from 'resolvers/callStateEntities';
 */
export const getCallerTypeNameFromValue = ( value ) => {

    // get callerTypeOptions from store
    const callerTypeOptions = get( store.getState()[ REDUCER.APP_CONTEXT ], 'component.data.options.callerType', [] );

    // get callerTypeOption
    const callerTypeOption = find( callerTypeOptions, { value: value } );

    // return the name of caller Type
    return get( callerTypeOption, 'name' );
};

/*************************/

/**
 * @desc Get redirection route based on the call state.
 * @example
 * import { getRedirectionRoute } from 'resolvers/callStateEntities';
 */
export const getRedirectionRoute = ( _callData = null ) => {

    // get call data
    const callData = isEmpty( _callData ) ? store.getState()[ REDUCER.CALL_STATE ] : _callData;

    if( isEmpty( callData ) ) {
        return {};
    }

    // get call information
    const callState = get( callData, 'callState', null );
   // const members = get( callData, 'members', [] );
    const members = get( callData, 'callerInfo.refId', null );
    const patientId = get( callData, 'callerInfo.patientId', null );

    switch( callState ) {

        // move to member management screen
        case CALL_ACTIVITY_STATE.ACTIVE: {
            if( ! isEmpty( members ) || ! isEmpty( patientId ) ) {
                return {
                    pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                    state: { memberId:  members ,patientId: patientId }
                };
            } else {
                return {
                    pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
                    search: searchQuery.stringify( {
                        step: 1,
                    } ),
                };
            }
        }

        // move to member verification screen
        case CALL_ACTIVITY_STATE.VERIFYING: {
            return {
                pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
                search: searchQuery.stringify( {
                    step: 2,
                } ),
            };
        }

        // move to `caller identification` step by default
        case CALL_ACTIVITY_STATE.AUTHORIZING:
        default: {
            return {
                pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
                search: searchQuery.stringify( {
                    step: 1,
                } ),
            };
        }
    }
};


/*************************/

/**
 * @desc Get redirection route based on the call state.
 * @example
 * import { getCallerTypeIcon } from 'resolvers/callStateEntities';
 */
export const getCallerTypeIcon = ( callerType ) => {

    // get name from the value
    const callerTypeName = getCallerTypeNameFromValue( callerType );

    switch( callerTypeName ) {

        // member caller
        case CALLER_TYPE_NAMES.PHARMACY: {
            return 'callerIconPharmacy';
        }

        // member caller
        case CALLER_TYPE_NAMES.CLIENT_REPRESENTATIVE: {
            return 'callerIconClient';
        }

        // member caller
        case CALLER_TYPE_NAMES.MEMBER:
        default: {
            return 'callerIconMember';
        }
    }
};
